import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/studio.scss';
import Seo from '../components/Seo';
import { PROJECTS } from '../data/projects';
import logoABC from '../assets/images/logo_ABC.webp';
import logoFutureTherapy from '../assets/images/logo_FutureTherapy.webp';
import logoGoodGrapes from '../assets/images/logo_GoodGrapes.webp';
import logoLlama from '../assets/images/logo_Llama.webp';
import logoMatteo from '../assets/images/logo_Matteo.webp';
import logoSonaris from '../assets/images/logo_Sonaris.webp';
import logoSuperGuage from '../assets/images/logo_SuperGuage.webp';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const SERVICE_GROUPS = [
  {
    label: 'Design',
    services: [
      { name: 'User Experience Design', slug: 'ux-design' },
      { name: 'User Interface Design', slug: 'ui-design' },
      { name: 'Web Design', slug: 'web-design' },
      { name: 'Brand Identity', slug: 'brand-identity' },
    ],
  },
  {
    label: 'Strategy',
    services: [
      { name: 'Idea Accelerator', slug: 'idea-accelerator' },
      { name: 'Product Development', slug: 'product-development' },
      { name: 'Fractional Design Services', slug: 'fractional-design-services' },
    ],
  },
  {
    label: 'Delivery',
    services: [
      { name: 'Web Development', slug: 'web-development' },
      { name: 'MVP Build', slug: 'mvp-development' },
    ],
  },
];

const Studio = () => {
  const [activeFaq, setActiveFaq] = useState(null);
  const studioHeroRef = useRef(null);
  const studioHeroContentRef = useRef(null);
  const serviceRows = useMemo(
    () =>
      SERVICE_GROUPS.flatMap((group) =>
        group.services.map((service) => ({ ...service, category: group.label }))
      ),
    []
  );
  
  const PARTNER_LOGOS = [
    { src: logoABC, alt: 'ABC' },
    { src: logoFutureTherapy, alt: 'Future Therapy' },
    { src: logoGoodGrapes, alt: 'Good Grapes' },
    { src: logoLlama, alt: 'Llama' },
    { src: logoMatteo, alt: 'Matteo' },
    { src: logoSonaris, alt: 'Sonaris' },
    { src: logoSuperGuage, alt: 'Super Guage' },
  ];
  
  const serviceGroups = [
    {
      label: 'Design',
      services: [
        {
          name: 'UX Design',
          description:
            'By understanding real user behaviour, I uncover pain points, simplify journeys, and craft intuitive experiences from the first interaction.',
          slug: 'ux-design',
        },
        {
          name: 'UI Design',
          description:
            'Form follows function. I design interfaces that are clear, consistent, and purposeful so teams can ship with confidence.',
          slug: 'ui-design',
        },
        {
          name: 'Web Design',
          description:
            'From strategy to execution, I create websites that balance business goals with user needs for fast, accessible experiences.',
          slug: 'web-design',
        },
        {
          name: 'Brand Identity',
          description:
            'Your brand is more than a logo—it’s the lasting impression. I build bold identities that are distinctive, coherent, and built to last.',
          slug: 'brand-identity',
        },
      ],
    },
    {
      label: 'Strategy',
      services: [
        {
          name: 'Idea Accelerator',
          description:
            'Accelerate ideas with strategic thinking, rapid prototyping, and validation so you ship something people actually want.',
          slug: 'idea-accelerator',
        },
        {
          name: 'Product Development',
          description:
            'I turn ideas into functional products through research, iteration, and collaboration with real user insight guiding every step.',
          slug: 'product-development',
        },
        {
          name: 'Fractional Design Services',
          description:
            'Embed senior design expertise into your team on a flexible basis for strategic design, rapid iteration, and ongoing optimisation.',
          slug: 'fractional-design-services',
        },
      ],
    },
    {
      label: 'Delivery',
      services: [
        {
          name: 'Web Development',
          description:
            'I build fast, scalable websites and applications that are reliable, maintainable, and ready to grow with your business.',
          slug: 'web-development',
        },
        {
          name: 'MVP Build',
          description:
            'Launch smarter with focused MVPs that validate ideas, attract users, and lay the groundwork for future growth.',
          slug: 'mvp-development',
        },
      ],
    },
  ];

  const carouselItems = useMemo(
    () =>
      PROJECTS.map((project) => {
        const heroBlock = project.blocks.find((block) => block.type === 'image');
        return {
          slug: project.slug,
          title: project.title,
          subtitle: project.subtitle,
          image: heroBlock ? heroBlock.src : null,
        };
      }).filter((project) => project.image),
    []
  );

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  useEffect(() => {
    const heroEl = studioHeroRef.current;
    const contentEl = studioHeroContentRef.current;
    if (!heroEl || !contentEl) return undefined;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentEl,
        { y: -20 },
        {
          y: 40,
          ease: 'none',
          scrollTrigger: {
            trigger: heroEl,
            start: 'top top',
            end: 'bottom top',
            scrub: true,
          },
        }
      );
    }, heroEl);

    return () => ctx.revert();
  }, []);

  const faqs = [
    {
      question: 'What types of projects do you typically work on?',
      answer: 'Across digital products including SaaS platforms, websites, public sector, healthcare and e-commerce. Typical clients also include startups aiming to drive business growth.'
    },
    {
      question: 'How long does a typical project take?',
      answer: 'Project timelines vary based on scope and complexity. A brand identity might take 4-6 weeks, while a full digital product could be 3-6 months. We always start with a discovery phase to define scope and set realistic expectations.'
    },
    {
      question: 'What\'s your design process like?',
      answer: 'I follow a collaborative process: discovery → research → strategy → design → testing → delivery. You\'re involved at every stage, with regular check-ins and opportunities for feedback to ensure we\'re building the right thing together.'
    },
    {
      question: 'Do you work with startups or established companies?',
      answer: 'Both! I love helping early-stage startups build their first product, but I also partner with established companies looking to innovate or improve existing experiences. Each brings unique challenges and opportunities.'
    }
  ];

  return (
    <>
      <Seo />
      <div className="studio-page">
      <div className="studio-hero" ref={studioHeroRef}>
        <div className="studio-hero__content" ref={studioHeroContentRef}>
          <h1 className="studio-hero__title">Bringing bold ideas to life</h1>
          <p className="studio-hero__subtitle">No egos. Pro kindness.</p>
        </div>
      </div>

      <section className="section intro">
        <div className="wrapper grid-display">
          <div className="row">

            <div className="col-12">
                <div className="blur-text blur-text--1 large center-text introtext">
 SaaS startups to hospitality, delivering engaging and valuable experiences           
      </div>
            </div>

     

          </div>
        </div>
      </section>

      <section className="section services-grid">
        <div className="wrapper">
          
          <ol className="services-list">
            {serviceRows.map((service, index) => (
              <li className="services-list__item" key={`${service.category}-${service.slug}`}>
                <Link to={`/studio/services/${service.slug}`} className="services-list__link">
                  <span className="services-list__number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="services-list__category mono">{service.category}</span>
                  <span className="services-list__name">{service.name}</span>
                  <span className="services-list__icon" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section partners-scroll">
        <div className="partners-scroll__track">
          <div className="partners-scroll__content">
            {PARTNER_LOGOS.map((logo, index) => (
              <div key={`partner-${index}`} className="partner-logo">
                <div className="partner-logo__placeholder">
                  <img src={logo.src} alt={`${logo.alt} logo`} />
                </div>
              </div>
            ))}
          </div>
          <div className="partners-scroll__content" aria-hidden="true">
            {PARTNER_LOGOS.map((logo, index) => (
              <div key={`partner-duplicate-${index}`} className="partner-logo">
                <div className="partner-logo__placeholder">
                  <img src={logo.src} alt="" aria-hidden="true" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section portfolio">
        <div className="wrapper">
          <div className="portfolio-heading-row">
            <h2 className="portfolio-heading">Latest Work</h2>
          </div>
        </div>
        <div className="wrapper grid-display">
          <div className="row">
            {carouselItems.slice(0, 2).map((item) => (
              <div className="portfolio-grid-item" key={item.slug}>
                <Link to={`/projects/${item.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="portfolio-grid-media">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="portfolio-grid-caption">
                    <div className="portfolio-grid-title">{item.title}</div>
                    <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
          <div className="row">
            {carouselItems.slice(2, 5).map((item) => (
              <div className="portfolio-grid-item" key={item.slug}>
                <Link to={`/projects/${item.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="portfolio-grid-media">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="portfolio-grid-caption">
                    <div className="portfolio-grid-title">{item.title}</div>
                    <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
          <div className="row full-height">
            {carouselItems.slice(5, 6).map((item) => (
              <div className="portfolio-grid-item" key={item.slug}>
                <Link to={`/projects/${item.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div className="portfolio-grid-media">
                    <img src={item.image} alt={item.title} />
                  </div>
                  <div className="portfolio-grid-caption">
                    <div className="portfolio-grid-title">{item.title}</div>
                    <div className="portfolio-grid-subtitle">{item.subtitle}</div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section faq">
        <div className="wrapper">
          <div className="row">

            <div className="col-5">
              <div className="blur-text blur-text--1 large introtext">
                Approach              
                </div>
            </div>

            <div className="col-1">
              
            </div>

            <div className="col-6">
              <div className="faq-list">
              {faqs.map((faq, index) => (
                <div key={index} className={`faq-item ${activeFaq === index ? 'active' : ''}`}>
                  <div className="faq-question" onClick={() => toggleFaq(index)}>
                    <h3>{faq.question}</h3>
                    <span className="faq-toggle">{activeFaq === index ? '×' : '+'}</span>
                  </div>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
            </div>


            
          </div>
        </div>
      </section>

    </div>
    </>
  );
};

export default Studio;
